import { WORLD_CURRENCIES } from '../data/currencies';

const CURRENCY_SYMBOL_MAP: Record<string, string> = {};
WORLD_CURRENCIES.forEach(c => { CURRENCY_SYMBOL_MAP[c.code] = c.symbol; });

/** The base currency all amounts are stored in on the backend. */
const BASE_CURRENCY = 'GBP';

/**
 * Map of ISO 3166 country codes to their primary ISO 4217 currency. Only
 * includes the currencies actually listed in WORLD_CURRENCIES — anything
 * missing from that list falls through to the base currency.
 */
const REGION_TO_CURRENCY: Record<string, string> = {
  // Europe
  GB: 'GBP', IE: 'EUR', DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR',
  BE: 'EUR', PT: 'EUR', AT: 'EUR', FI: 'EUR', GR: 'EUR', LU: 'EUR', MT: 'EUR',
  CY: 'EUR', SK: 'EUR', SI: 'EUR', EE: 'EUR', LV: 'EUR', LT: 'EUR', HR: 'EUR',
  CH: 'CHF', NO: 'NOK', SE: 'SEK', DK: 'DKK', PL: 'PLN', CZ: 'CZK', HU: 'HUF',
  RO: 'RON', BG: 'BGN', RU: 'RUB', UA: 'UAH',
  // Americas
  US: 'USD', CA: 'CAD', MX: 'MXN', BR: 'BRL', AR: 'ARS', CO: 'COP', CL: 'CLP',
  PE: 'PEN',
  // Asia-Pacific
  AU: 'AUD', NZ: 'NZD', JP: 'JPY', CN: 'CNY', HK: 'HKD', TW: 'TWD', KR: 'KRW',
  SG: 'SGD', MY: 'MYR', TH: 'THB', ID: 'IDR', PH: 'PHP', VN: 'VND', IN: 'INR',
  PK: 'PKR', BD: 'BDT', LK: 'LKR', NP: 'NPR',
  // Middle East
  AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR', JO: 'JOD',
  IL: 'ILS', TR: 'TRY', EG: 'EGP',
  // Africa
  NG: 'NGN', GH: 'GHS', KE: 'KES', ZA: 'ZAR', TZ: 'TZS', UG: 'UGX', MA: 'MAD',
};

/**
 * Guess the user's preferred currency from the browser locale. e.g. a device
 * set to en-NZ returns "NZD". Falls back to the base currency when we can't
 * determine the region or the region isn't in our supported list.
 */
export const guessLocaleCurrency = (): string => {
  try {
    if (typeof navigator === 'undefined') return BASE_CURRENCY;
    const candidates: string[] = [];
    if (navigator.language) candidates.push(navigator.language);
    if (Array.isArray(navigator.languages)) candidates.push(...navigator.languages);
    for (const locale of candidates) {
      // Prefer Intl.Locale when available (handles "en", "en-NZ", "en-Latn-NZ").
      let region: string | undefined;
      try {
        // @ts-ignore - Intl.Locale exists on all supported platforms
        region = new Intl.Locale(locale).region;
      } catch {
        const match = locale.match(/[-_]([A-Za-z]{2})(?:$|[-_])/);
        region = match?.[1];
      }
      if (region) {
        const code = REGION_TO_CURRENCY[region.toUpperCase()];
        if (code) return code;
      }
    }
  } catch {}
  return BASE_CURRENCY;
};

/**
 * Read the user's saved currency code from localStorage. If nothing has been
 * saved yet, guess from the browser locale so a user in New Zealand sees NZD
 * by default instead of GBP.
 */
export const getSavedCurrency = (): string => {
  try {
    const stored = localStorage.getItem('fh-budget-currency') || localStorage.getItem('fh-currency');
    if (stored) return stored;
  } catch {}
  return guessLocaleCurrency();
};

/** Return the symbol for a given currency code. */
export const getCurrencySymbol = (code?: string): string => {
  const c = code || getSavedCurrency();
  return CURRENCY_SYMBOL_MAP[c] || c;
};

// ── Exchange rate cache ──

interface RateCache {
  rates: Record<string, number>;
  fetchedAt: number;
}

let rateCache: RateCache | null = null;
const RATE_CACHE_TTL = 6 * 60 * 60 * 1000; // 6 hours
const RATE_STORAGE_KEY = 'fh-exchange-rates';
let rateListeners: (() => void)[] = [];

/** Subscribe to rate updates (for React components to re-render). */
export const onRatesLoaded = (cb: () => void) => {
  rateListeners.push(cb);
  return () => { rateListeners = rateListeners.filter(l => l !== cb); };
};

const loadCachedRates = (): RateCache | null => {
  try {
    const stored = localStorage.getItem(RATE_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as RateCache;
    if (Date.now() - parsed.fetchedAt < RATE_CACHE_TTL && parsed.rates) return parsed;
    return null;
  } catch { return null; }
};

/** Fetch exchange rates from multiple free APIs (fallback chain). */
export const fetchExchangeRates = async (): Promise<Record<string, number>> => {
  if (rateCache && Date.now() - rateCache.fetchedAt < RATE_CACHE_TTL) {
    return rateCache.rates;
  }

  const stored = loadCachedRates();
  if (stored) {
    rateCache = stored;
    return stored.rates;
  }

  const apis = [
    `https://latest.currency-api.pages.dev/v1/currencies/${BASE_CURRENCY.toLowerCase()}.json`,
    `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${BASE_CURRENCY.toLowerCase()}.json`,
  ];

  for (const url of apis) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(timer);
      if (!res.ok) continue;
      const data = await res.json();
      const rawRates: Record<string, number> = data[BASE_CURRENCY.toLowerCase()] || {};

      if (Object.keys(rawRates).length < 5) continue; // bad response

      const rates: Record<string, number> = { [BASE_CURRENCY]: 1 };
      for (const [code, rate] of Object.entries(rawRates)) {
        rates[code.toUpperCase()] = rate as number;
      }

      rateCache = { rates, fetchedAt: Date.now() };
      try { localStorage.setItem(RATE_STORAGE_KEY, JSON.stringify(rateCache)); } catch {}
      rateListeners.forEach(cb => cb());
      return rates;
    } catch { continue; }
  }

  // All APIs failed — return 1:1
  return { [BASE_CURRENCY]: 1 };
};

/** Get exchange rate (sync — uses cached rates). */
export const getExchangeRate = (targetCurrency?: string): number => {
  const target = targetCurrency || getSavedCurrency();
  if (target === BASE_CURRENCY) return 1;
  if (!rateCache) {
    const stored = loadCachedRates();
    if (stored) rateCache = stored;
  }
  return rateCache?.rates[target] || 1;
};

/** Convert sub-units (pence) from base to target currency. */
export const convertPence = (pence: number, targetCurrency?: string): number => {
  return Math.round(pence * getExchangeRate(targetCurrency));
};

/** Format sub-units with conversion. */
export const formatPence = (pence: number | string, currencyCode?: string): string => {
  const code = currencyCode || getSavedCurrency();
  const symbol = getCurrencySymbol(code);
  const converted = convertPence(Number(pence), code);
  return `${symbol}${(converted / 100).toFixed(2)}`;
};

/** Format whole-unit price with conversion. */
export const formatPrice = (price: number, currencyCode?: string): string => {
  const code = currencyCode || getSavedCurrency();
  const symbol = getCurrencySymbol(code);
  const rate = getExchangeRate(code);
  const converted = price * rate;
  if (converted < 1) {
    const sub = Math.round(converted * 100);
    if (code === 'GBP') return `${sub}p`;
    return `${symbol}0.${String(sub).padStart(2, '0')}`;
  }
  return `${symbol}${converted.toFixed(2)}`;
};

/** Convert sub-units to decimal string with conversion. */
export const penceToDecimal = (p: number, currencyCode?: string): string => {
  const converted = convertPence(p, currencyCode);
  return (converted / 100).toFixed(2);
};
